import { Co2MaterialType, Co2MaterialTypeByCategory, Co2MaterialTypeOptions, Co2MaterialTypesClient, UpsertCo2MaterialTypeRequest, UpsertCo2MaterialTypeResponse } from "@api/apiClient";
import axios from "@api/axiosInstance";
import { queryClient } from "@hooks/useGlobalState";
import { useMutation, useQuery } from "@tanstack/react-query";

const co2MaterialTypesClient = new Co2MaterialTypesClient(undefined, axios);

export const useCo2MaterialTypes = (materialLibraryIds: string[] | undefined = undefined, showArchived: boolean = false, options?: any) => {
  return useQuery<Co2MaterialType[]>({
    ...options,
    queryKey: ["materialTypes", materialLibraryIds?.join(","), showArchived],
    queryFn: async () => co2MaterialTypesClient.getMaterialTypes(materialLibraryIds, showArchived),
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 60 minutes
  });
};

export const useCo2MaterialTypeOptions = (materialLibraryIds: string[] | undefined = undefined, showArchived: boolean = false, options?: any) => {
  return useQuery<Co2MaterialTypeOptions>({
    ...options,
    queryKey: ["materialTypeOptions", materialLibraryIds?.join(","), showArchived],
    queryFn: async () => co2MaterialTypesClient.getMaterialTypeOptions(materialLibraryIds, showArchived),
    placeholderData: [],
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 60 minutes
  });
};

export const useUpsertCo2MaterialType = (options?: any) => {
  return useMutation<UpsertCo2MaterialTypeResponse, Error, UpsertCo2MaterialTypeRequest[]>({
    ...options,
    mutationFn: (upsertCo2MaterialTypeRequests: UpsertCo2MaterialTypeRequest[]) => co2MaterialTypesClient.upsertMaterialTypes(upsertCo2MaterialTypeRequests),
    onSuccess: (data: UpsertCo2MaterialTypeResponse) => {
      if (data.failureItems?.length === 0) {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["materialLibraries"], exact: false });
          queryClient.invalidateQueries({ queryKey: ["materialTypes"], exact: false });
          queryClient.invalidateQueries({ queryKey: ["materialTypeOptions"], exact: false });
        }, 0);
      }

      options?.onSuccess?.(data);
    }
  });
};

export const useDeleteCo2MaterialType = (options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (co2MaterialTypeId: string) => co2MaterialTypesClient.deleteMaterialType(co2MaterialTypeId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["materialTypes"], exact: false });
      await queryClient.invalidateQueries({ queryKey: ["materialTypeOptions"], exact: false });

      options?.onSuccess?.();
    }
  });
};

export const useCo2MaterialTypeByCategory = (options?: any) => {
  return useQuery<Co2MaterialTypeByCategory[]>({
    ...options,
    queryKey: ["co2-material-type-by-category"],
    queryFn: async () => co2MaterialTypesClient.co2MaterialTypeByCategory(),
  });
};